import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';
import { renderContent } from '../renderContent';
import { SubjectIcon } from '../SubjectIcons';
import ActionBtn from '../admin/ActionBtn';

const BASE = process.env.REACT_APP_API_BASE_URL;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function slugify(str) {
    return (str ?? '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export function exerciseThemeSlug(ex, idx) {
    return ex.theme?.trim() ? slugify(ex.theme) : `declinaison-${idx + 1}`;
}

function srsLabel(skill) {
    if (!skill.attempt_count) return { label: 'Nouveau', color: 'text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 border-cyan-500/30' };
    const today = new Date().toISOString().slice(0, 10);
    if (skill.next_review_date && skill.next_review_date <= today) return { label: 'À réviser', color: 'text-orange-600 dark:text-orange-400 bg-orange-500/10 border-orange-500/30' };
    const score = parseFloat(skill.best_score ?? 0);
    if (score >= 80) return { label: 'Maîtrisé', color: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30' };
    return { label: 'En cours', color: 'text-purple-600 dark:text-purple-400 bg-purple-500/10 border-purple-500/30' };
}

export function effectiveCriteria(exercise) {
    return [
        ...(exercise.active_criteria   ?? []),
        ...(exercise.specific_criteria ?? []),
    ];
}

// ─── SubjectTiles ─────────────────────────────────────────────────────────────

function SubjectTiles({ skills, loading, onSelectSubject }) {
    const today = new Date().toISOString().slice(0, 10);

    // Build subject groups (unlocked only for counts)
    const subjectMap = {};
    skills.forEach(sk => {
        const name = sk.subject_name || 'Général';
        if (!subjectMap[name]) subjectMap[name] = { total: 0, due: 0, locked: 0 };
        if (sk.locked) {
            subjectMap[name].locked += 1;
        } else {
            subjectMap[name].total += 1;
            if (!sk.attempt_count || (sk.next_review_date && sk.next_review_date <= today)) {
                subjectMap[name].due += 1;
            }
        }
    });

    const subjectNames = Object.keys(subjectMap).sort();

    const totalDue = skills.filter(sk => !sk.locked && (!sk.attempt_count || (sk.next_review_date && sk.next_review_date <= today))).length;

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="flex items-center gap-4 mb-8">
                <div className="flex-1">
                    <h1 className="text-2xl font-extrabold text-gray-800 dark:text-white">Exercices</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Maîtrise les méthodes par la pratique</p>
                </div>
                {totalDue > 0 && (
                    <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-600 dark:text-orange-400">
                        {totalDue} à réviser
                    </span>
                )}
            </div>

            {loading && <div className="text-center text-gray-400 py-16">Chargement...</div>}

            {!loading && subjectNames.length === 0 && (
                <div className="text-center text-gray-400 py-16">
                    <p className="text-4xl mb-4">📐</p>
                    <p className="font-semibold">Aucun exercice disponible pour l'instant.</p>
                </div>
            )}

            {!loading && subjectNames.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {subjectNames.map(subject => {
                        const { total, due, locked } = subjectMap[subject];
                        return (
                            <div key={subject}
                                onClick={() => onSelectSubject(subject)}
                                className="group relative bg-white/40 dark:bg-black/10 rounded-3xl p-6 border border-gray-200/50 dark:border-white/[0.05] cursor-pointer hover:border-cyan-500/40 hover:shadow-[0_0_30px_rgba(6,182,212,0.12)] transition-all duration-200 hover:-translate-y-1 flex flex-col items-center text-center overflow-hidden">
                                <div className="absolute -top-8 -right-8 w-20 h-20 bg-cyan-500/10 blur-[40px] rounded-full pointer-events-none group-hover:bg-cyan-500/25 transition-colors duration-500" />
                                <div className="bg-cyan-100 dark:bg-cyan-900/30 w-14 h-14 rounded-2xl flex items-center justify-center mb-4 relative z-10 shadow-sm group-hover:scale-105 transition-transform duration-200">
                                    <SubjectIcon subject={subject} className="w-7 h-7" />
                                </div>
                                <h2 className="text-lg font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400 relative z-10 leading-tight mb-2">
                                    {subject}
                                </h2>
                                <div className="flex items-center gap-2 flex-wrap justify-center relative z-10">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {total} exercice{total !== 1 ? 's' : ''}
                                    </span>
                                    {due > 0 && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-600 dark:text-orange-400">
                                            {due} à réviser
                                        </span>
                                    )}
                                    {locked > 0 && (
                                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                            🔒 {locked}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── SkillsList ───────────────────────────────────────────────────────────────

export function SkillsList({ skills, filterSubject, onClearSubject, overrideSkills, chapterName, onGoToSkill, compact, isAdmin }) {
    const navigate = useNavigate();

    const filtered = overrideSkills ?? skills.filter(sk => (sk.subject_name || 'Général') === filterSubject);
    const unlocked = filtered.filter(sk => isAdmin || !sk.locked);
    const locked   = isAdmin ? [] : filtered.filter(sk => sk.locked);

    const goToSkill = (sk) => {
        if (onGoToSkill) { onGoToSkill(sk); return; }
        const subjectSlug = slugify(sk.subject_name || 'general');
        const skillSlug   = slugify(sk.title);
        navigate(`/exercice/${subjectSlug}/${skillSlug}`, { state: { skillId: sk.id } });
    };

    return (
        <div className={`max-w-3xl mx-auto ${compact ? '' : 'px-4 py-8'}`}>
            {/* Header with back to subjects */}
            {!compact && (
            <div className="flex items-center gap-3 mb-6">
                <button onClick={onClearSubject}
                    className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors font-semibold">
                    ← {chapterName ? 'Retour au cours' : 'Matières'}
                </button>
                <span className="text-gray-300 dark:text-gray-600">/</span>
                <div className="flex items-center gap-2">
                    {!chapterName && <SubjectIcon subject={filterSubject} className="w-4 h-4" />}
                    <h1 className="text-lg font-extrabold text-gray-800 dark:text-white">{chapterName ?? filterSubject}</h1>
                </div>
            </div>
            )}

            {filtered.length === 0 && (
                <div className="text-center text-gray-400 py-16">
                    <p className="text-4xl mb-4">📐</p>
                    <p className="font-semibold">Aucun exercice pour cette matière.</p>
                </div>
            )}

            {/* Unlocked exercises */}
            {unlocked.length > 0 && (
                <div className="flex flex-col gap-3 mb-6">
                    {unlocked.map(sk => {
                        const { label, color } = srsLabel(sk);
                        return (
                            <button key={sk.id} onClick={() => goToSkill(sk)}
                                className="w-full text-left bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-2xl p-5 border border-gray-200 dark:border-white/[0.05] hover:border-cyan-500/30 hover:bg-white dark:hover:bg-white/[0.05] transition-all duration-200 shadow-sm group">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-800 dark:text-white text-sm group-hover:text-cyan-700 dark:group-hover:text-cyan-400 transition-colors truncate">{sk.title}</p>
                                        {sk.best_score !== null && (
                                            <span className="text-xs text-gray-400">Meilleur : {Math.round(sk.best_score)}%</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${color}`}>{label}</span>
                                        <span className="text-gray-300 dark:text-gray-600 group-hover:text-cyan-500 transition-colors text-lg">›</span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Locked exercises */}
            {locked.length > 0 && (
                <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
                        🔒 Exercices verrouillés
                    </p>
                    <div className="flex flex-col gap-3">
                        {locked.map(sk => (
                            <div key={sk.id}
                                className="w-full text-left bg-gray-50 dark:bg-white/[0.01] rounded-2xl p-5 border border-gray-200 dark:border-white/[0.05] opacity-60 cursor-default">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-500 dark:text-gray-500 text-sm truncate">{sk.title}</p>
                                        {sk.required_notions?.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {sk.required_notions.map(n => (
                                                    <span key={n.id} className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${n.mastered ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400' : 'bg-gray-100 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-400'}`}>
                                                        {n.mastered ? '✓' : '○'} {n.number ? `${n.number} – ` : ''}{n.name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-gray-300 dark:text-gray-600 text-lg shrink-0">🔒</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── SkillDetail ──────────────────────────────────────────────────────────────

export function SkillDetail({ skill, onStartExercise, user, editUrl, hideHeader }) {
    const [modelOpen, setModelOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isAdmin = ['admin', 'supertuteur'].includes(user?.role);

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            {!hideHeader && (
                <div className="mb-8 flex items-start justify-between gap-4">
                    <div>
                        {skill.subject_name && <p className="text-xs font-bold uppercase tracking-widest text-cyan-600 dark:text-cyan-400 mb-1">{skill.subject_name}</p>}
                        <h1 className="text-2xl font-extrabold text-gray-800 dark:text-white">{skill.title}</h1>
                    </div>
                    {isAdmin && editUrl && (
                        <ActionBtn
                            variant="edit"
                            onClick={() => navigate(editUrl, { state: { skillTitle: skill.title, fromUrl: location.pathname } })}
                            icon={<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>}
                        >
                            Modifier
                        </ActionBtn>
                    )}
                </div>
            )}

            {/* Exercice modèle */}
            {(skill.model_question || skill.model_explanation || skill.model_video_url) && (
                <div className="mb-6 rounded-2xl border border-purple-500/30 bg-purple-500/5 dark:bg-purple-500/[0.04] overflow-hidden">
                    <button onClick={() => setModelOpen(o => !o)}
                        className="w-full flex items-center justify-between p-5 text-left">
                        <div className="flex items-center gap-3">
                            <span className="text-xl">📖</span>
                            <div>
                                <p className="font-bold text-gray-800 dark:text-white text-sm">Exercice modèle</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Découvre la méthode avant de pratiquer</p>
                            </div>
                        </div>
                        <span className={`text-gray-400 transition-transform duration-200 ${modelOpen ? 'rotate-180' : ''}`}>▾</span>
                    </button>

                    {modelOpen && (
                        <div className="px-5 pb-6 flex flex-col gap-5 border-t border-purple-500/20">
                            {skill.model_question && (
                                <div className="mt-4">
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2">Énoncé</p>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{renderContent(skill.model_question)}</div>
                                </div>
                            )}
                            {skill.model_explanation && (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2">Méthode de rédaction</p>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed bg-white/60 dark:bg-white/[0.03] rounded-xl p-4 border border-gray-200 dark:border-white/[0.06]">{renderContent(skill.model_explanation)}</div>
                                </div>
                            )}
                            {skill.model_correction && (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-2">Correction modèle</p>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">{renderContent(skill.model_correction)}</div>
                                </div>
                            )}
                            {skill.model_video_url && (
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2">Vidéo explicative</p>
                                    <a href={skill.model_video_url} target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 text-sm font-semibold hover:bg-red-500/20 transition-colors">
                                        ▶ Regarder la vidéo
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Critères globaux */}
            {skill.criteria?.length > 0 && (
                <div className="mb-6 rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-white/70 dark:bg-white/[0.02] p-5">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">Critères de réussite généraux</p>
                    <ul className="flex flex-col gap-2">
                        {skill.criteria.map(c => (
                            <li key={c.id} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                                <span className="mt-0.5 shrink-0 w-4 h-4 rounded border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center" />
                                <span>{c.label}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Déclinaisons */}
            <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">Déclinaisons</p>
                <div className="flex flex-col gap-3">
                    {skill.exercises?.map((ex, idx) => {
                        const crit = effectiveCriteria(ex);
                        return (
                            <div key={ex.id}
                                className="bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-2xl p-5 border border-gray-200 dark:border-white/[0.06]">
                                <div className="flex items-start justify-between gap-4 mb-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">Déclinaison {idx + 1}</span>
                                            {ex.theme && (
                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400">{ex.theme}</span>
                                            )}
                                            {crit.length > 0 && (
                                                <span className="text-[10px] text-gray-400">{crit.length} critère{crit.length !== 1 ? 's' : ''}</span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{ex.question}</p>
                                    </div>
                                    <button onClick={() => onStartExercise(skill, ex, idx)}
                                        className="shrink-0 px-4 py-2 rounded-xl text-sm font-bold bg-cyan-500/10 border border-cyan-500/30 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-500/20 transition-colors">
                                        Commencer
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ─── SkillExercise ────────────────────────────────────────────────────────────

export function SkillExercise({ skill, exercise, onBack, hideBackButton, hideHeader }) {
    const [answerMode, setAnswerMode]         = useState('photo');
    const [textAnswer, setTextAnswer]         = useState('');
    const [imageFile, setImageFile]           = useState(null);
    const [imagePreview, setImagePreview]     = useState(null);
    const [showCorrection, setShowCorrection] = useState(false);
    const [isSubmitting, setIsSubmitting]     = useState(false);
    const [result, setResult]                 = useState(null);
    const [notification, setNotification]     = useState(null);
    const [selfChecked, setSelfChecked]       = useState(new Set()); // self-evaluation
    const fileInputRef = useRef(null);

    const criteria = effectiveCriteria(exercise);

    const toggleSelf = (id) => {
        setSelfChecked(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleImageSelect = (file) => {
        if (!file) return;
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) handleImageSelect(file);
    };

    const handleSubmit = async () => {
        if (!imageFile && !textAnswer.trim()) {
            setNotification({ type: 'error', text: "Ajoute une photo ou écris ta réponse." });
            setTimeout(() => setNotification(null), 3000);
            return;
        }
        setIsSubmitting(true);
        try {
            let imagePath = null;
            if (imageFile) {
                const formData = new FormData();
                formData.append('image', imageFile);
                const uploadRes  = await apiClient(`${BASE}/upload_answer.php`, { method: 'POST', body: formData });
                const uploadData = await uploadRes.json();
                if (!uploadData.success) throw new Error(uploadData.message || "Erreur d'upload.");
                imagePath = uploadData.path;
            }

            const evalRes  = await apiClient(`${BASE}/save_skill_attempt.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    skill_id: skill.id, exercise_id: exercise.id,
                    answer_text: textAnswer.trim() || null,
                    answer_image_path: imagePath,
                }),
            });
            const evalData = await evalRes.json();
            if (!evalData.success) throw new Error(evalData.message || "Erreur d'évaluation.");
            setResult(evalData);
        } catch (err) {
            setNotification({ type: 'error', text: err.message });
            setTimeout(() => setNotification(null), 4000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAskAxio = () => {
        const context = `Je travaille sur l'exercice "${skill.title}"${exercise.theme ? ` (${exercise.theme})` : ''}.\n\nÉnoncé :\n${exercise.question}`;
        sessionStorage.setItem('axio-prefill', context);
        window.dispatchEvent(new Event('open-axio-chat'));
    };

    const aiValidatedCount = result
        ? Object.values(result.criteria_results).filter(Boolean).length
        : 0;

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            {!hideBackButton && (
                <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors mb-6 font-semibold">
                    ← Retour à l'exercice
                </button>
            )}

            {/* Header */}
            {!hideHeader && (
                <div className="mb-6">
                    {skill.subject_name && <p className="text-xs font-bold uppercase tracking-widest text-cyan-600 dark:text-cyan-400 mb-1">{skill.subject_name}</p>}
                    <h1 className="text-xl font-extrabold text-gray-800 dark:text-white mb-1">{skill.title}</h1>
                    {exercise.theme && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400">{exercise.theme}</span>
                    )}
                </div>
            )}

            {/* Énoncé */}
            <div className="mb-6 bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl p-6 border border-gray-200 dark:border-white/[0.08] shadow-sm">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">Énoncé</p>
                <div className="text-gray-800 dark:text-gray-200 leading-relaxed">{renderContent(exercise.question)}</div>
            </div>

            {/* Critères — auto-évaluation interactive */}
            {criteria.length > 0 && (
                <div className="mb-6 rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-white/70 dark:bg-white/[0.02] overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                            Critères de réussite
                        </p>
                        <span className="text-xs text-gray-400">
                            {selfChecked.size}/{criteria.length} cochés
                        </span>
                    </div>
                    <ul className="px-5 py-3 flex flex-col gap-2">
                        {criteria.map(c => {
                            const checked = selfChecked.has(c.id);
                            const aiResult = result ? (result.criteria_results?.[String(c.id)] ?? null) : null;
                            return (
                                <li key={c.id}
                                    onClick={() => toggleSelf(c.id)}
                                    className={`flex items-start gap-3 text-sm cursor-pointer rounded-lg px-2 py-1 -mx-2 transition-colors ${checked ? 'bg-emerald-50 dark:bg-emerald-900/10' : 'hover:bg-gray-50 dark:hover:bg-white/[0.03]'}`}>
                                    {/* Self-eval checkbox */}
                                    <span className={`mt-0.5 shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${checked ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 dark:border-gray-600'}`}>
                                        {checked && <span className="text-[9px] font-black">✓</span>}
                                    </span>
                                    <span className={`flex-1 ${checked ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>{c.label}</span>
                                    {/* AI result badge (shown after eval) */}
                                    {aiResult !== null && (
                                        <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${aiResult ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-500 dark:text-red-400'}`}>
                                            IA {aiResult ? '✓' : '✗'}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    {!result && (
                        <p className="px-5 pb-3 text-[10px] text-gray-400 italic">Coche les critères que tu penses avoir respectés (auto-évaluation)</p>
                    )}
                </div>
            )}

            {/* Zone de réponse (masquée si résultat affiché) */}
            {!result && (
                <>
                    <div className="flex gap-2 mb-4">
                        <button onClick={() => setAnswerMode('photo')}
                            className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-colors ${answerMode === 'photo' ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-700 dark:text-cyan-400' : 'border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.04]'}`}>
                            📷 Photo de ma copie
                        </button>
                        <button onClick={() => setAnswerMode('text')}
                            className={`flex-1 py-2.5 rounded-xl text-sm font-bold border transition-colors ${answerMode === 'text' ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-700 dark:text-cyan-400' : 'border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.04]'}`}>
                            ✏️ Répondre par écrit
                        </button>
                    </div>

                    {answerMode === 'photo' ? (
                        <div onDrop={handleDrop} onDragOver={e => e.preventDefault()}
                            onClick={() => fileInputRef.current?.click()}
                            className="mb-4 rounded-2xl border-2 border-dashed border-gray-300 dark:border-white/[0.1] hover:border-cyan-500/50 transition-colors cursor-pointer flex flex-col items-center justify-center min-h-[200px] bg-gray-50 dark:bg-white/[0.02] relative overflow-hidden">
                            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                                onChange={e => handleImageSelect(e.target.files?.[0])} />
                            {imagePreview ? (
                                <img src={imagePreview} alt="Aperçu" className="max-h-[400px] object-contain rounded-xl" />
                            ) : (
                                <div className="flex flex-col items-center gap-3 p-8 text-center">
                                    <span className="text-4xl">📷</span>
                                    <p className="font-semibold text-gray-600 dark:text-gray-400 text-sm">Prends une photo de ta copie</p>
                                    <p className="text-xs text-gray-400 dark:text-gray-600">ou glisse une image ici</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <textarea value={textAnswer} onChange={e => setTextAnswer(e.target.value)}
                            placeholder="Rédige ta réponse ici..." rows={8}
                            className="w-full mb-4 bg-white/70 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-2xl p-5 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-600 outline-none focus:border-cyan-500/50 resize-y transition-colors" />
                    )}

                    <div className="flex flex-col gap-3">
                        <button onClick={handleSubmit}
                            disabled={isSubmitting || (!imageFile && !textAnswer.trim())}
                            className="w-full py-4 rounded-2xl font-bold text-white bg-gradient-to-r from-cyan-500 to-purple-600 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                            {isSubmitting ? 'Évaluation en cours...' : 'Faire évaluer ma réponse'}
                        </button>
                        <div className="flex gap-3">
                            <button onClick={() => setShowCorrection(s => !s)}
                                className="flex-1 py-3 rounded-xl text-sm font-semibold border border-gray-200 dark:border-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors">
                                {showCorrection ? 'Masquer la correction' : 'Voir la correction'}
                            </button>
                            <button onClick={handleAskAxio}
                                className="flex-1 py-3 rounded-xl text-sm font-semibold border border-cyan-500/30 bg-cyan-500/5 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-500/10 transition-colors">
                                Demander à Axio
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Résultat IA */}
            {result && (
                <div className="flex flex-col gap-5 animate-fade-in">
                    <div className="rounded-2xl border p-6 text-center bg-white/70 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08]">
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2">Score obtenu</p>
                        <p className={`text-5xl font-extrabold mb-1 ${result.score >= 80 ? 'text-emerald-600 dark:text-emerald-400' : result.score >= 50 ? 'text-orange-500' : 'text-red-500'}`}>
                            {Math.round(result.score)}%
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{aiValidatedCount} / {criteria.length} critères validés par l'IA</p>
                        {criteria.length > 0 && (
                            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">Auto-évaluation : {selfChecked.size}/{criteria.length}</p>
                        )}
                        <div className="mt-4 w-full bg-gray-200 dark:bg-white/[0.05] rounded-full h-2 overflow-hidden">
                            <div className={`h-2 rounded-full transition-all duration-700 ${result.score >= 80 ? 'bg-emerald-500' : result.score >= 50 ? 'bg-orange-500' : 'bg-red-500'}`}
                                style={{ width: `${result.score}%` }} />
                        </div>
                    </div>

                    {result.feedback && (
                        <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-5">
                            <p className="text-xs font-bold uppercase tracking-widest text-cyan-600 dark:text-cyan-400 mb-2">Retour d'Axio</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{result.feedback}</p>
                        </div>
                    )}

                    <div className="flex flex-col gap-3">
                        <button onClick={() => setShowCorrection(s => !s)}
                            className="w-full py-3 rounded-xl text-sm font-semibold border border-gray-200 dark:border-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors">
                            {showCorrection ? 'Masquer la correction' : 'Voir la correction'}
                        </button>
                        <div className="flex gap-3">
                            <button onClick={handleAskAxio}
                                className="flex-1 py-3 rounded-xl text-sm font-semibold border border-cyan-500/30 bg-cyan-500/5 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-500/10 transition-colors">
                                Discuter avec Axio
                            </button>
                            <button onClick={() => { setResult(null); setImageFile(null); setImagePreview(null); setTextAnswer(''); setShowCorrection(false); setSelfChecked(new Set()); }}
                                className="flex-1 py-3 rounded-xl text-sm font-semibold border border-gray-200 dark:border-white/[0.06] text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors">
                                Réessayer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Correction */}
            {showCorrection && exercise.correction && (
                <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 animate-fade-in">
                    <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-3">Correction</p>
                    <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{renderContent(exercise.correction)}</div>
                </div>
            )}

            {notification && (
                <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-xl font-bold text-white shadow-lg z-50 ${notification.type === 'success' ? 'bg-emerald-500/90 border border-emerald-400' : 'bg-red-500/90 border border-red-400'}`}>
                    {notification.text}
                </div>
            )}
        </div>
    );
}

// ─── SkillsView (router) ──────────────────────────────────────────────────────

export default function SkillsView() {
    const location = useLocation();
    const navigate = useNavigate();

    const [skills, setSkills]       = useState([]);
    const [loading, setLoading]     = useState(true);
    const [filterSubject, setFilterSubject] = useState('');
    const [detailSkill, setDetailSkill]     = useState(null); // loaded skill detail
    const [detailLoading, setDetailLoading] = useState(false);
    const [chapterSkills, setChapterSkills] = useState(null); // non-null when filtered by chapter
    const [chapterLoading, setChapterLoading] = useState(false);
    const didAutoNavigate = useRef(false);

    // Chapter filter from location state (set when navigating from CourseView tab)
    const chapterId   = location.state?.chapterId ?? null;
    const chapterName = location.state?.chapterName ?? null;

    // Parse URL path: /exercice/[subjectSlug]/[skillSlug]/[themeSlug]
    const parts = location.pathname.replace(/^\/exercice\/?/, '').split('/').filter(Boolean);
    const [subjectSlugUrl, skillSlugUrl, themeSlugUrl] = parts;
    const isDetailView   = parts.length >= 2;
    const isExerciseView = parts.length >= 3;

    // Load full skills list
    useEffect(() => {
        apiClient(`${BASE}/get_skills.php`, { method: 'GET' })
            .then(r => r.json())
            .then(d => { if (d.success) setSkills(d.skills); })
            .finally(() => setLoading(false));
    }, []);

    // Load chapter-filtered skills when chapterId is provided
    useEffect(() => {
        if (!chapterId || isDetailView) return;
        didAutoNavigate.current = false;
        setChapterLoading(true);
        apiClient(`${BASE}/get_skills.php?chapter_id=${chapterId}`)
            .then(r => r.json())
            .then(d => { if (d.success) setChapterSkills(d.skills); })
            .finally(() => setChapterLoading(false));
    }, [chapterId, isDetailView]);

    // Auto-navigate when chapter filter returns exactly 1 unlocked skill
    useEffect(() => {
        if (!chapterSkills || didAutoNavigate.current || isDetailView) return;
        const unlocked = chapterSkills.filter(sk => !sk.locked);
        if (unlocked.length !== 1) return;
        const sk = unlocked[0];
        const subjectSlug = slugify(sk.subject_name || 'general');
        const skillSlug   = slugify(sk.title);
        didAutoNavigate.current = true;
        // Load detail to check exercise count
        apiClient(`${BASE}/get_skills.php?id=${sk.id}`)
            .then(r => r.json())
            .then(d => {
                if (d.success && d.skill.exercises?.length === 1) {
                    const ex = d.skill.exercises[0];
                    navigate(`/exercice/${subjectSlug}/${skillSlug}/${exerciseThemeSlug(ex, 0)}`, {
                        state: { exerciseId: ex.id },
                        replace: true,
                    });
                } else {
                    navigate(`/exercice/${subjectSlug}/${skillSlug}`, {
                        state: { skillId: sk.id },
                        replace: true,
                    });
                }
            });
    }, [chapterSkills, isDetailView, navigate]);

    // Find skill from URL slugs (once list is loaded)
    const matchedSkill = skills.find(sk =>
        slugify(sk.subject_name || 'general') === subjectSlugUrl &&
        slugify(sk.title) === skillSlugUrl
    );

    // Load skill detail when navigating to detail/exercise view
    useEffect(() => {
        if (!isDetailView || !matchedSkill) return;
        setDetailLoading(true);
        apiClient(`${BASE}/get_skills.php?id=${matchedSkill.id}`, { method: 'GET' })
            .then(r => r.json())
            .then(d => { if (d.success) setDetailSkill(d.skill); })
            .finally(() => setDetailLoading(false));
    }, [isDetailView, matchedSkill?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleStartExercise = (skill, exercise, idx) => {
        const subjectSlug = slugify(skill.subject_name || 'general');
        const skillSlug   = slugify(skill.title);
        const themeSlug   = exerciseThemeSlug(exercise, idx);
        navigate(`/exercice/${subjectSlug}/${skillSlug}/${themeSlug}`, { state: { exerciseId: exercise.id } });
    };

    const handleBack = useCallback(() => {
        if (isExerciseView) {
            navigate(`/exercice/${subjectSlugUrl}/${skillSlugUrl}`);
        } else if (isDetailView) {
            navigate('/exercice');
        } else {
            navigate('/');
        }
    }, [navigate, isDetailView, isExerciseView, subjectSlugUrl, skillSlugUrl]);

    // ── Exercise view ─────────────────────────────────────────────────────────
    if (isExerciseView && detailSkill) {
        const exercise = detailSkill.exercises?.find(ex => exerciseThemeSlug(ex, detailSkill.exercises.indexOf(ex)) === themeSlugUrl)
            ?? detailSkill.exercises?.find((ex, i) => exerciseThemeSlug(ex, i) === themeSlugUrl);
        if (exercise) {
            return <SkillExercise skill={detailSkill} exercise={exercise} onBack={handleBack} />;
        }
    }

    // ── Detail view ───────────────────────────────────────────────────────────
    if (isDetailView) {
        if (detailLoading || (matchedSkill && !detailSkill)) {
            return <div className="text-center text-gray-400 py-16 max-w-3xl mx-auto px-4 pt-8">Chargement...</div>;
        }
        if (!matchedSkill || !detailSkill) {
            return (
                <div className="max-w-3xl mx-auto px-4 py-8">
                    <button onClick={() => navigate('/exercice')} className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline mb-6 block">← Retour</button>
                    <div className="text-center text-gray-400 py-16">Exercice introuvable.</div>
                </div>
            );
        }
        return (
            <div>
                <div className="max-w-3xl mx-auto px-4 pt-6">
                    <button onClick={handleBack} className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors font-semibold">
                        ← Retour aux exercices
                    </button>
                </div>
                <SkillDetail skill={detailSkill} onStartExercise={handleStartExercise} />
            </div>
        );
    }

    // ── List view ─────────────────────────────────────────────────────────────

    // Chapter-filtered list view
    if (chapterSkills && !isDetailView) {
        if (chapterLoading) {
            return <div className="text-center text-gray-400 py-16 max-w-3xl mx-auto px-4 pt-8">Chargement...</div>;
        }
        const unlocked = chapterSkills.filter(sk => !sk.locked);
        // Only show the list if there are multiple skills (auto-nav handles single)
        if (unlocked.length !== 1 || didAutoNavigate.current) {
            return (
                <SkillsList
                    skills={[]}
                    overrideSkills={chapterSkills}
                    chapterName={chapterName}
                    onClearSubject={() => { setChapterSkills(null); navigate('/apprendre'); }}
                />
            );
        }
        return <div className="text-center text-gray-400 py-16 max-w-3xl mx-auto px-4 pt-8">Chargement...</div>;
    }

    if (filterSubject) {
        return (
            <SkillsList
                skills={skills}
                filterSubject={filterSubject}
                onClearSubject={() => setFilterSubject('')}
            />
        );
    }

    return (
        <SubjectTiles
            skills={skills}
            loading={loading}
            onSelectSubject={setFilterSubject}
        />
    );
}
